import {
  getBundledOfficialThemeById,
  getModuleThemeIdFromLocalStorageSync,
} from '@acv/theme-tokens';

type ThemeLike = {
  id?: string;
  name?: string;
  family?: string;
  colors?: Record<string, string>;
};

const MODULE_ID = 'aiRecording';

function readJson(raw: string | null): any {
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function getUserEmail(): string | null {
  try {
    const direct = localStorage.getItem('userEmail');
    if (direct) return direct;

    const user = readJson(localStorage.getItem('user'));
    if (typeof user?.mail === 'string') return user.mail;
    if (typeof user?.email === 'string') return user.email;

    const msg = readJson(localStorage.getItem('msg'));
    if (typeof msg?.user?.mail === 'string') return msg.user.mail;
    if (typeof msg?.user?.email === 'string') return msg.user.email;
  } catch {
    return null;
  }
  return null;
}

function getBundledByModuleStorage(): ThemeLike | null {
  try {
    const raw = getModuleThemeIdFromLocalStorageSync([MODULE_ID]);
    const themeId =
      typeof raw === 'string'
        ? raw
        : (raw && typeof raw === 'object' && typeof (raw as any).themeId === 'string'
          ? (raw as any).themeId
          : null);
    if (!themeId) return null;
    const bundled = getBundledOfficialThemeById(themeId);
    return bundled && bundled.colors ? (bundled as ThemeLike) : null;
  } catch {
    return null;
  }
}

function readCachedTheme(): ThemeLike | null {
  try {
    const email = getUserEmail();
    const byUserKey = email ? `${MODULE_ID}_theme_cache:${email}` : null;
    const byUser = byUserKey ? readJson(localStorage.getItem(byUserKey)) : null;
    if (byUser?.id && byUser?.colors) return byUser as ThemeLike;

    const generic = readJson(localStorage.getItem(`${MODULE_ID}_theme_cache`));
    if (generic?.id && generic?.colors) return generic as ThemeLike;
  } catch {
    return null;
  }
  return null;
}

function applyPrepaintTheme(theme: ThemeLike): void {
  if (!theme?.colors) return;
  const colors = theme.colors;
  const root = document.documentElement;
  const family = theme.family || 'conservative';
  const background = colors.background || '#ffffff';
  const foreground = colors.foreground || '#1a1a1a';
  const border = colors.border || '#e0e0e0';
  const primary = colors.primary || '#1976d2';
  const card = colors.card || background;
  const navbar = colors.navbar || background;
  const sidebar = colors.sidebarBg || card || background;
  const hover = colors.cardHoverBg || 'rgba(0, 0, 0, 0.05)';
  const active = colors.cardActiveBg || primary;
  const selectedFg = family === 'playful' ? '#000000' : '#ffffff';

  root.setAttribute('data-theme-family', family);
  // Text on theme-coloured buttons and the theme colour as text, as useExtensionTheme sets them
  // (the share page runs on this pre-paint step only): the theme's own button text when it has one
  // (e.g. black on High Contrast Dark's white), and the text colour for playful themes (lime on lime).
  const onPrimary = colors.buttonPrimaryForeground || selectedFg;
  root.style.setProperty('--button-primary-foreground', onPrimary);
  root.style.setProperty('--dropdown-item-selected-foreground', onPrimary);
  root.style.setProperty('--primary-text', family === 'playful' ? foreground : primary);
  root.style.setProperty('--theme-background', background);
  root.style.setProperty('--theme-foreground', foreground);
  root.style.setProperty('--theme-primary', primary);
  root.style.setProperty('--theme-border', border);
  root.style.setProperty('--theme-card', card);
  root.style.setProperty('--theme-navbar', navbar);
  root.style.setProperty('--theme-toolbar', colors.toolbar || navbar);

  root.style.setProperty('--bg-primary', background);
  root.style.setProperty('--text-primary', foreground);
  // `muted` is a surface colour in ACV themes; secondary text is `mutedForeground` (as in useExtensionTheme).
  root.style.setProperty('--text-secondary', colors.mutedForeground || colors.muted_foreground || '#666666');
  root.style.setProperty('--card-bg', card);
  root.style.setProperty('--navbar-bg', navbar);
  root.style.setProperty('--sidebar-bg', sidebar);
  root.style.setProperty('--sidebar-background', sidebar);
  root.style.setProperty('--border-color', border);

  // Variables clave para evitar “flash” en paneles/selecciones.
  root.style.setProperty('--surface-hover', hover);
  root.style.setProperty('--surface-selected', primary);
  root.style.setProperty('--surface-selected-foreground', selectedFg);
  root.style.setProperty('--card-active-bg', active);
  root.style.setProperty('--card-active-foreground', foreground);
  root.style.setProperty('--sidebar-item-hover-bg', hover);
  root.style.setProperty('--sidebar-item-selected-bg', colors.sidebarItemSelectedBg || primary);
  root.style.setProperty('--sidebar-item-selected-foreground', selectedFg);
  root.style.setProperty('--controls-bg', colors.controlsBg || navbar || card);
  root.style.setProperty('--controlsBg', colors.controlsBg || navbar || card);
  root.style.setProperty('--controls-foreground', colors.controlsForeground || foreground);
  root.style.setProperty('--controlsForeground', colors.controlsForeground || foreground);

  root.style.backgroundColor = background;
  root.style.color = foreground;
  document.body.style.backgroundColor = background;
  document.body.style.color = foreground;
  const appRoot = document.getElementById('root');
  if (appRoot) {
    appRoot.style.backgroundColor = family === 'playful' ? background : 'transparent';
  }

  (window as any).__acvPreloadedTheme = theme;
}

try {
  const cached = readCachedTheme();
  if (cached) {
    applyPrepaintTheme(cached);
  } else {
    const bundled = getBundledByModuleStorage();
    if (bundled) applyPrepaintTheme(bundled);
  }
} catch {
  // ignore prepaint bootstrap errors
}

